import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Clock, Briefcase } from "lucide-react";
import { PERKS, OPEN_ROLES, HIRING_NOTE } from "../../../data/careers";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Perks = () => {
  return (
    <section
      data-testid="careers-perks"
      className="py-20 md:py-28 border-t border-ink/10 bg-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-forest-accent mb-4">/ 02 — Why Milestone</div>
            <h2
              className="font-display font-bold leading-[1.02] tracking-tight text-ink text-balance"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
            >
              Less corporate.
              <br />
              <span className="italic font-light text-amber-accent">More chemistry.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.k}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i}
              data-testid={`perk-${i}`}
              className="bg-bone p-7 md:p-9 group hover:bg-white transition-colors min-h-[200px] flex flex-col"
            >
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-ink-soft mb-auto">
                {p.k}
              </div>
              <div className="mt-6">
                <div className="font-display font-medium text-ink text-lg md:text-xl leading-tight mb-2">
                  {p.title}
                </div>
                <p className="text-ink-soft text-sm leading-relaxed">{p.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const OpenRoles = ({ onApply }) => {
  return (
    <section
      id="open-roles"
      data-testid="careers-open-roles"
      className="scroll-mt-nav py-20 md:py-28 border-t border-ink/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="overline text-forest-accent mb-4">/ 03 — Open Roles</div>
            <h2
              className="font-display font-bold leading-[1.02] tracking-tight text-ink"
              style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
            >
              We're hiring.
            </h2>
          </div>
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-ink-soft">
            {OPEN_ROLES.length} open · updated regularly
          </div>
        </div>

        <div className="border-t border-ink/15">
          {OPEN_ROLES.map((role, i) => (
            <motion.article
              key={role.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i}
              data-testid={`open-role-${role.id}`}
              className="group border-b border-ink/10 py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-6 items-start hover:bg-white/60 transition-colors px-2 -mx-2 md:px-4 md:-mx-4"
            >
              <div className="col-span-12 md:col-span-5">
                <div className="font-display font-medium text-ink text-2xl md:text-3xl leading-tight tracking-tight">
                  {role.title}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-ink-soft">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {role.location}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {role.department}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {role.type} · {role.experience}
                  </span>
                </div>
              </div>
              <div className="col-span-12 md:col-span-5">
                <p className="text-ink-soft text-[15px] leading-relaxed">{role.summary}</p>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:justify-end">
                <button
                  type="button"
                  onClick={() => onApply(role)}
                  data-testid={`apply-btn-${role.id}`}
                  className="inline-flex items-center gap-1.5 bg-ink text-bone px-5 py-2.5 text-sm font-medium rounded-sm hover:bg-ink/85 transition-all group/btn"
                >
                  Apply
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* General application */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 items-center bg-white border border-ink/10 p-8 md:p-12">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-amber-accent mb-3">General application</div>
            <div className="font-display font-medium text-ink text-2xl md:text-3xl leading-tight mb-2">
              {HIRING_NOTE.title}
            </div>
            <p className="text-ink-soft leading-relaxed max-w-2xl">{HIRING_NOTE.body}</p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <button
              type="button"
              onClick={() =>
                onApply({
                  id: "general",
                  title: "General Application",
                  location: "Any location",
                  department: "Open",
                  experience: "—",
                })
              }
              data-testid="apply-btn-general"
              className="inline-flex items-center gap-1.5 border border-ink text-ink px-5 py-2.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-bone transition-all"
            >
              Send general application
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
